/**
 * R2 Playlist Adapter
 *
 * Adapter that implements PlaylistGateway interface.
 * Wraps existing R2MusicService and PlaylistCacheService.
 *
 * Clean Architecture:
 * - adapt-gateway-abstraction: Hides R2/cache details behind domain gateway
 * - dep-interface-ownership: Implements interface owned by domain layer
 * - adapt-anti-corruption-layer: Translates external formats to domain entities
 */

import type { Playlist as DomainPlaylist } from '@/domain/entities';
import type { PlaylistGateway, FetchPlaylistResult } from '@/domain/gateways';
import { PlaylistMapper } from '../mappers';
import { getR2MusicService } from '@/app/services/music/R2MusicService';
import { getPlaylistCacheService } from '@/app/services/music/PlaylistCacheService';
import {
  PlaylistFetchService,
  createR2NetworkFetcher,
} from '@/app/services/music/PlaylistFetchService';
import { Logger } from '@/app/utils/logger';

const LOG_TAG = 'r2-playlist-adapter';

/**
 * Adapter implementing PlaylistGateway using R2 bucket and localStorage cache.
 *
 * Uses PlaylistFetchService for SWR-style fetching:
 * - Returns cached data immediately if available
 * - Refreshes stale data in background
 * - Falls back to cache on network failure
 */
export class R2PlaylistAdapter implements PlaylistGateway {
  private fetchService: PlaylistFetchService | null = null;
  private initError: Error | null = null;

  constructor() {
    this.initFetchService();
  }

  /**
   * Initialize the PlaylistFetchService with required dependencies.
   * Handles missing R2 URL gracefully by storing the error.
   */
  private initFetchService(): void {
    const r2BucketUrl = import.meta.env.VITE_R2_MUSIC_BUCKET_URL;

    if (!r2BucketUrl) {
      this.initError = new Error(
        'VITE_R2_MUSIC_BUCKET_URL not configured - R2 playlist service unavailable'
      );
      Logger.warn(this.initError.message, LOG_TAG);
      return;
    }

    try {
      const r2Service = getR2MusicService();
      const cacheGateway = getPlaylistCacheService();
      const networkFetcher = createR2NetworkFetcher((etag?: string) =>
        r2Service.fetchPlaylistConditional(etag)
      );

      this.fetchService = new PlaylistFetchService({
        cacheGateway,
        networkFetcher,
      });

      Logger.info('R2PlaylistAdapter initialized successfully', LOG_TAG);
    } catch (error) {
      this.initError =
        error instanceof Error
          ? error
          : new Error(`Failed to init PlaylistFetchService: ${error}`);
      Logger.error(this.initError.message, LOG_TAG);
    }
  }

  /**
   * Fetch the playlist from R2 bucket with caching.
   *
   * @param options.forceRefresh - If true, bypass cache and fetch from network
   * @returns FetchPlaylistResult with domain Playlist entity
   * @throws Error if R2 URL not configured or service unavailable
   */
  async fetchPlaylist(options?: {
    forceRefresh?: boolean;
  }): Promise<FetchPlaylistResult> {
    if (!this.fetchService) {
      throw (
        this.initError ||
        new Error('PlaylistFetchService not available - R2 URL not configured')
      );
    }

    Logger.info(
      `Fetching playlist (forceRefresh=${options?.forceRefresh ?? false})`,
      LOG_TAG
    );

    const result = options?.forceRefresh
      ? await this.fetchService.forceRefresh()
      : await this.fetchService.fetch();

    // Map legacy playlist to domain entity
    const domainPlaylist = PlaylistMapper.toDomainPlaylist(result.data);

    Logger.info(
      `Playlist fetched: ${domainPlaylist.tracks.length} tracks, source=${result.source}`,
      LOG_TAG
    );

    return {
      playlist: domainPlaylist,
      source: result.source as 'network' | 'cache' | 'fallback',
      isStale: result.isStale,
      etag: result.etag,
    };
  }

  /**
   * Get the currently cached playlist without network request.
   *
   * @returns Cached domain Playlist or null if no cache exists
   */
  getCachedPlaylist(): DomainPlaylist | null {
    const cacheService = getPlaylistCacheService();
    const cacheResult = cacheService.get();

    if (!cacheResult.data) {
      Logger.debug('No cached playlist available', LOG_TAG);
      return null;
    }

    Logger.debug(
      `Returning cached playlist (age=${cacheResult.age}ms, stale=${cacheResult.isStale})`,
      LOG_TAG
    );

    return PlaylistMapper.toDomainPlaylist(cacheResult.data);
  }

  /**
   * Clear all cached playlist data.
   */
  clearCache(): void {
    const cacheService = getPlaylistCacheService();
    cacheService.clear();
    Logger.info('Playlist cache cleared', LOG_TAG);
  }

  /**
   * Check if the adapter is properly initialized.
   */
  isAvailable(): boolean {
    return this.fetchService !== null;
  }

  /**
   * Get initialization error if any.
   */
  getInitError(): Error | null {
    return this.initError;
  }
}

// ============================================================================
// Singleton Factory
// ============================================================================

let playlistAdapterInstance: R2PlaylistAdapter | null = null;

/**
 * Get singleton R2PlaylistAdapter instance.
 * Creates the adapter on first call.
 */
export function getR2PlaylistAdapter(): R2PlaylistAdapter {
  if (!playlistAdapterInstance) {
    playlistAdapterInstance = new R2PlaylistAdapter();
  }
  return playlistAdapterInstance;
}

/**
 * Reset singleton instance (for testing).
 */
export function resetR2PlaylistAdapter(): void {
  playlistAdapterInstance = null;
}
